import { Badge, Container, Grid, Group, Loader, Paper, SimpleGrid, Table, Text, Title } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { PageHeader } from "@/components/PageHeader"
import { queries } from "@/lib/queries"

const PLAYFAIR = "'Playfair Display', Georgia, serif"

export const Route = createFileRoute("/companies/$id/qsbs")({
  component: CompanyQsbs,
})

function CompanyQsbs() {
  const { id } = Route.useParams()
  const { data, isPending, isError } = useQuery(queries.companyQsbs(id))

  if (isPending) return <Loader />
  if (isError) return <Text c="var(--pitch-negative)">Failed to load QSBS data</Text>

  // status is Option<{ qsbsEligible, incorporationDate: Option<string> }> from the query boundary.
  const qsbsEligible = data.status.fold(
    () => false,
    (s) => s.qsbsEligible,
  )
  const incorporationDate = data.status
    .flatMap((s) => s.incorporationDate)
    .fold(
      () => "—",
      (d) => new Date(d).toLocaleDateString(),
    )

  return (
    <Container size="xl">
      <PageHeader eyebrow="Section 1202">
        <Group gap="md" align="center">
          <span>QSBS Status — {id.toUpperCase()}</span>
          <Badge
            size="lg"
            radius={0}
            color={
              data.trafficLight === "green"
                ? "pitchPositive"
                : data.trafficLight === "yellow"
                  ? "pitchInflection"
                  : "pitchNegative"
            }
          >
            {data.trafficLight.toUpperCase()}
          </Badge>
        </Group>
      </PageHeader>

      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="xl">
        <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
          <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
            QSBS Eligible
          </Text>
          <Text
            ff={PLAYFAIR}
            fz={26}
            fw={500}
            mt={6}
            c={qsbsEligible ? "var(--pitch-positive)" : "var(--pitch-negative)"}
            style={{ lineHeight: 1.1 }}
          >
            {qsbsEligible ? "Yes" : "No"}
          </Text>
        </Paper>
        <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
          <Text fz={11} lts="0.08em" c="var(--pitch-text-secondary)" tt="uppercase" fw={600}>
            Incorporation Date
          </Text>
          <Text
            ff={PLAYFAIR}
            fz={20}
            fw={500}
            mt={6}
            c="var(--pitch-text-primary)"
            className="pitch-tabular"
            style={{ lineHeight: 1.2 }}
          >
            {incorporationDate}
          </Text>
        </Paper>
      </SimpleGrid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Compliance Checks
            </Title>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Requirement</Table.Th>
                  <Table.Th>Compliant</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.complianceChecks.map((c) => (
                  <Table.Tr key={c.requirement}>
                    <Table.Td>{c.requirement}</Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={c.compliant === "Y" ? "pitchPositive" : "pitchNegative"}>
                        {c.compliant}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius={0} style={{ borderColor: "var(--pitch-border)" }}>
            <Title
              order={4}
              ff={PLAYFAIR}
              fw={500}
              c="var(--pitch-text-primary)"
              mb="sm"
              style={{ letterSpacing: "-0.005em" }}
            >
              Tax Savings
            </Title>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Investment ($M)</Table.Th>
                  <Table.Th>Exit Value ($M)</Table.Th>
                  <Table.Th>Tax Savings ($M)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.taxSavings.map((t, i) => (
                  <Table.Tr key={i}>
                    <Table.Td className="pitch-tabular">{t.investmentM}</Table.Td>
                    <Table.Td className="pitch-tabular">{t.exitValueM}</Table.Td>
                    <Table.Td fw={700} className="pitch-tabular">
                      {t.taxSavingsM}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  )
}
